import dayjs from 'dayjs';
import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Container,
  Label,
} from 'reactstrap';

import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import Meta from 'components/App/Meta';
import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import Pagination from 'components/Pagination';
import giftApi, {
  USER_GIFT_STATUS,
  getUserGiftStatusLabel,
} from 'api/giftApi';
import { themeColor } from 'constants/index';
import ENDPOINT from 'constants/endPoint';
import cn from 'utils/classnames';
import { handleError } from 'utils';

import BcoinIcon from 'assets/images/Bcoin.svg';
import styles from './ManagerGiftExchange.module.scss';

const TITLE = 'Danh sách đổi quà';

const breadcrumbList = [
  {
    name: 'Quà tặng',
    isActive: false,
    link: ENDPOINT.MANAGER_GIFT_LIST,
  },
  {
    name: TITLE,
    isActive: true,
    link: ENDPOINT.MANAGER_GIFT_EXCHANGE,
  },
];

const TAB_KEY = {
  ALL: 'ALL',
  PENDING: 'PENDING',
  SENT: 'SENT',
  ERROR: 'ERROR',
};

const NAV_TAB = [
  { key: TAB_KEY.ALL, title: 'Tất cả' },
  { key: TAB_KEY.PENDING, title: 'Chờ gửi quà' },
  { key: TAB_KEY.SENT, title: 'Đã gửi quà' },
  { key: TAB_KEY.ERROR, title: 'Lỗi' },
];

const PENDING_TAB_STATUSES = [
  USER_GIFT_STATUS.PENDING,
  USER_GIFT_STATUS.UNUSED,
];

const SENT_TAB_STATUSES = [
  USER_GIFT_STATUS.DELIVERED,
  USER_GIFT_STATUS.RECEIVED,
  USER_GIFT_STATUS.USED,
];

const TAB_API_STATUS = {
  [TAB_KEY.PENDING]: PENDING_TAB_STATUSES,
  [TAB_KEY.SENT]: SENT_TAB_STATUSES,
  [TAB_KEY.ERROR]: USER_GIFT_STATUS.EXPIRED,
};

const GIFT_TYPE_FILTER = {
  EXCLUSIVE: 'EXCLUSIVE',
  VOUCHER: 'VOUCHER',
  LIMITED: 'LIMITED',
};

const GIFT_TYPE_FILTER_OPTIONS = [
  { value: GIFT_TYPE_FILTER.EXCLUSIVE, label: 'Quà Độc Quyền' },
  { value: GIFT_TYPE_FILTER.VOUCHER, label: 'Quà Voucher' },
  { value: GIFT_TYPE_FILTER.LIMITED, label: 'Quà giới hạn' },
];

const DEFAULT_LIMIT = 10;

const EXCHANGE_STATUS_DISPLAY = {
  [USER_GIFT_STATUS.PENDING]: { label: 'Đang chờ xử lý', variant: 'pending' },
  [USER_GIFT_STATUS.UNUSED]: { label: 'Chưa sử dụng', variant: 'pending' },
  [USER_GIFT_STATUS.USED]: { label: 'Đã sử dụng', variant: 'sent' },
  [USER_GIFT_STATUS.DELIVERED]: { label: 'Đã gửi', variant: 'sent' },
  [USER_GIFT_STATUS.RECEIVED]: { label: 'Đã nhận', variant: 'sent' },
  [USER_GIFT_STATUS.EXPIRED]: { label: 'Hết hạn', variant: 'error' },
};

const getExchangeStatusDisplay = (status) => (
  EXCHANGE_STATUS_DISPLAY[status] || {
    label: getUserGiftStatusLabel(status),
    variant: 'pending',
  }
);

const formatTransactionId = (id) => (id ? `#${id}` : '—');

const formatExchangeDate = (date) => (date ? dayjs(date).format('D/M/YYYY') : '—');

const formatCoinValue = (item) => {
  const value = item.coin ?? item.gift?.coin ?? 0;
  return new Intl.NumberFormat('vi-VN').format(value);
};

function ManagerGiftExchange() {
  const [userGifts, setUserGifts] = useState({ data: [], total: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState(TAB_KEY.ALL);
  const [pendingCount, setPendingCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [submittedFilters, setSubmittedFilters] = useState({
    q: '',
    giftType: '',
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: submittedFilters.q,
      giftType: submittedFilters.giftType,
    },
    onSubmit: (values) => {
      setSubmittedFilters({
        q: values.q.trim(),
        giftType: values.giftType,
      });
      setCurrentPage(1);
    },
  });

  const onChangeFilterOption = useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const onResetForm = useCallback(() => {
    setSubmittedFilters({ q: '', giftType: '' });
    validation.resetForm({
      values: { q: '', giftType: '' },
    });
    setCurrentPage(1);
  }, [validation]);

  const isSearchBtnDisabled = useMemo(
    () => validation.values.q.trim() === '' && validation.values.giftType === '',
    [validation.values.q, validation.values.giftType],
  );

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await giftApi.getUserGifts({
        status: PENDING_TAB_STATUSES,
        page: 1,
        limit: 1,
      });
      const result = response?.payload ?? response;
      setPendingCount(result?.total || 0);
    } catch {
      setPendingCount(0);
    }
  }, []);

  const fetchUserGifts = useCallback(async () => {
    try {
      setIsLoading(true);
      const payload = {
        page: currentPage,
        limit: DEFAULT_LIMIT,
      };

      if (submittedFilters.q) {
        payload.q = submittedFilters.q;
      }

      if (submittedFilters.giftType) {
        if (submittedFilters.giftType === GIFT_TYPE_FILTER.LIMITED) {
          payload.isFlashSale = true;
        } else {
          payload.category = submittedFilters.giftType;
        }
      }

      const apiStatus = TAB_API_STATUS[activeTabKey];
      if (apiStatus) {
        payload.status = apiStatus;
      }

      const response = await giftApi.getUserGifts(payload);
      const result = response?.payload ?? response;

      setUserGifts({
        data: result?.data || [],
        total: result?.total || result?.data?.length || 0,
      });
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  }, [activeTabKey, currentPage, submittedFilters]);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  useEffect(() => {
    fetchUserGifts();
  }, [fetchUserGifts]);

  const onChangeTab = useCallback((tabKey) => () => {
    setActiveTabKey(tabKey);
    setCurrentPage(1);
  }, []);

  const getTabLabel = useCallback((tab) => {
    if (tab.key === TAB_KEY.PENDING && pendingCount > 0) {
      return `${tab.title} (${pendingCount})`;
    }

    return tab.title;
  }, [pendingCount]);

  const onChangePage = useCallback((selected) => {
    setCurrentPage(selected.selected + 1);
  }, []);

  const totalItems = userGifts.total || 0;
  const totalPage = Math.ceil(totalItems / DEFAULT_LIMIT);
  const paginatedUserGifts = userGifts.data || [];

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={TITLE} />

      <Container fluid>
        <Breadcrumb pageTitle={TITLE} breadcrumbList={breadcrumbList} />

        <Loading loading={isLoading} loaderOnPage />

        <Card className="Section">
          <form onSubmit={validation.handleSubmit}>
            <div className={styles.FilterRow}>
              <div className={styles.FilterFields}>
                <div className={styles.FilterSearchCol}>
                  <Label className="Section__Search__Label">
                    Tìm kiếm
                  </Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm kiếm theo Mã giao dịch, hoặc số điện thoại khách hàng"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input', styles.FilterSearchInput)}
                    />

                    <i className="fas fa-search Section__Search__Icon__Search" />
                  </div>
                </div>

                <div className={styles.FilterSelectCol}>
                  <Label className="Section__Search__Label">
                    Bộ lọc
                  </Label>

                  <AppSelectValidation
                    placeholder="Theo Loại quà"
                    name="giftType"
                    keyName="giftType"
                    options={GIFT_TYPE_FILTER_OPTIONS}
                    validation={validation}
                    value={validation.values.giftType}
                    onChange={onChangeFilterOption}
                    isHiddenSearchable
                    isHiddenClearable
                    selectedClassName={styles.GiftTypeSelect}
                  />
                </div>
              </div>

              <div className={styles.FilterActionsCol}>
                <Button
                  outline
                  classes="Section__Search__Button__ReType mr-24"
                  color={themeColor.LIGHT}
                  onClick={onResetForm}
                >
                  Nhập lại
                </Button>

                <Button
                  classes="Section__Search__Button__Search"
                  type="submit"
                  disabled={isSearchBtnDisabled}
                >
                  Tìm kiếm
                </Button>
              </div>
            </div>
          </form>
        </Card>

        <Card className="Section">
          <div className="Tab__Header__Container">
            <div className="Tab__Header">
              <nav className="Tab__Header__Nav" aria-label="Lọc trạng thái đổi quà">
                {NAV_TAB.map((tab) => (
                  <div key={tab.key} className="Tab__Header__Tab">
                    <button
                      type="button"
                      onClick={onChangeTab(tab.key)}
                      className={`Tab__Header__Tab__Item ${
                        activeTabKey === tab.key && 'Active'
                      }`}
                    >
                      {getTabLabel(tab)}
                    </button>
                  </div>
                ))}
              </nav>
            </div>
          </div>

          <Label className="Table__Body__Label">
            {`${totalItems} lượt đổi quà`}
          </Label>

          <div className="w-100" style={{ overflow: 'auto' }}>
            <table className="table custom_table">
              <thead>
                <tr>
                  <th style={{ minWidth: '140px' }}>Mã giao dịch</th>
                  <th style={{ minWidth: '348px' }}>Tên khách hàng</th>
                  <th style={{ minWidth: '348px' }}>Quà tặng</th>
                  <th style={{ minWidth: '160px' }}>Giá Bcoin</th>
                  <th style={{ minWidth: '160px' }}>Số lượng</th>
                  <th style={{ minWidth: '160px' }}>Thời gian đổi</th>
                  <th style={{ minWidth: '160px' }}>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUserGifts.map((item) => {
                  const statusDisplay = getExchangeStatusDisplay(item.status);
                  const transactionId = item.userGiftId;

                  return (
                    <tr key={item._id} style={{ cursor: 'default' }}>
                      <td style={{ width: '140px', maxWidth: '140px' }}>
                        <div className={styles.TransactionCell}>
                          <div className={styles.TransactionId}>
                            {formatTransactionId(transactionId)}
                          </div>
                          <Link
                            to={ENDPOINT.MANAGER_GIFT_EXCHANGE_DETAIL(item._id)}
                            className="button-action button-link text-start p-0 d-block"
                          >
                            Xem chi tiết
                          </Link>
                        </div>
                      </td>

                      <td style={{ width: '348px', maxWidth: '348px' }}>
                        <div className={styles.CustomerCell}>
                          {item.user?.avatar ? (
                            <img
                              src={item.user.avatar}
                              alt={item.fullName || item.user?.fullName}
                              className={styles.CustomerAvatar}
                            />
                          ) : (
                            <div className={styles.CustomerAvatarPlaceholder} />
                          )}
                          <div className={styles.CustomerInfo}>
                            <div className={styles.CustomerName}>
                              {item.fullName || item.user?.fullName || '—'}
                            </div>
                            <div className={styles.CustomerPhone}>
                              {item.phoneNumber
                                || item.user?.phoneNumber
                                || item.user?.phone
                                || '—'}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={{ width: '348px', maxWidth: '348px' }}>
                        <div className={styles.GiftName}>
                          {item.gift?.name || '—'}
                        </div>
                      </td>

                      <td style={{ width: '160px', maxWidth: '160px' }}>
                        <div className={styles.BcoinCell}>
                          <img src={BcoinIcon} alt="Bcoin" width="18" height="18" />
                          <span>
                            {formatCoinValue(item)}
                            {' '}
                            Bcoin
                          </span>
                        </div>
                      </td>
                      <td style={{ width: '160px', maxWidth: '160px' }}>
                        <div className={styles.ExchangeDate}>
                          {item.quantity}
                        </div>
                      </td>
                      <td style={{ width: '160px', maxWidth: '160px' }}>
                        <div className={styles.ExchangeDate}>
                          {formatExchangeDate(item.createdAt)}
                        </div>
                      </td>
                      <td style={{ width: '160px', maxWidth: '160px' }}>
                        <span
                          className={`${styles.StatusBadge} ${styles[`StatusBadge--${statusDisplay.variant}`]}`}
                        >
                          {statusDisplay.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {paginatedUserGifts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center">
                      Không có dữ liệu
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Pagination
          page={currentPage}
          pageCount={totalPage}
          onChangePage={onChangePage}
        />
      </Container>
    </div>
  );
}

export default ManagerGiftExchange;
